//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  BV_NETWORK_MESSAGES,
  BV_TERMS_AND_CONDITIONS_URL,
  GENERAL_MESSAGING_TIKTOK,
  RIGHTS_REQUEST_LOCALES,
  RightsOptionErrorState,
  RightsOptionFormState,
  RightsOptionsLoadingState,
  RightsRequestConfigurationStep,
  SelectedTermAndConditions,
  TIKTOK_NETWORK_MESSAGES,
  TIKTOK_TAG_US_PHRASES,
  TIKTOK_TAG_US_SHORT_PHRASES,
  TncOptionsTexts,
  TncTitles,
  getTikTokRetailerMessage,
  getTikTokTagUsShort,
  getTikTokTagUsSuffix,
} from '@account-settings/components/rights-requests/constants/rightRequestConstants';
import { SetupMessagingTrackingIds } from '@account-settings/components/rights-requests/constants/setupMessagingPrefix';
import { ITermsAndConditions } from '@apis/rights-management/rightsManagementApiModels';
import { SecondaryButtonStyles } from '@components/atoms/button/buttonStyles';
import {
  IRightsOption,
  IRightsRequestDatum,
  RightsRequestTargetType,
} from '@legacy-components/rights-management/request-rights-model';
import ClientId from '@models/ClientId';
import { UIA_RIGHTS_REQUEST_PERMISSION } from '@uia/constants/uiaConstants';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { escapeRegexSpecialChars } from '@utils/regexUtils';
import { hasTagUsPhrase } from '@utils/social-text-utils/socialTextUtils';
import { hashtagRegex } from '@utils/stringUtils';
import {
  URL_PROTOCOL_PATTERN_WITH_WWW,
  getUrlWithoutProtocol,
} from '@utils/urlUtils';
import isValidUrl from '@utils/urlValidation';

import { Locales } from 'app/constants/locales/locale';
import { termsConditionsSettings } from 'app/constants/rights-management/terms-and-conditions-settings';

const CustomLinkTexts = TncOptionsTexts[SelectedTermAndConditions.CustomLink];

export const appendTagUsSuffixToApprovalPhrase = (
  message: string,
  approvalPhrase: string,
  languageCode: Locales = Locales.EN_US,
): string => {
  const tagUsSuffix = getTikTokTagUsSuffix(languageCode);

  // If approval phrase is not in the message, return message with suffix appended at the end
  if (!approvalPhrase || !message.includes(approvalPhrase)) {
    return `${message.trim()} ${tagUsSuffix}`.trim();
  }

  const parts = message.split(approvalPhrase);

  if (parts.length > 1) {
    const beforePhrase = (parts[0] ?? '').trim();
    const afterPhrase = parts.slice(1).join(approvalPhrase).trim();

    const tagUsShort = getTikTokTagUsShort(languageCode);
    if (hasTagUsPhrase(message, [tagUsShort, tagUsSuffix])) {
      return message;
    }

    // Remove trailing English "reply" / "reply with" overlap, then the locale equivalent.
    let cleanedBefore = beforePhrase
      .replace(/\s*\breply\b(\s+with)?\s*$/i, '')
      .trim();

    if (tagUsShort) {
      const replyWithEquivalent = tagUsSuffix
        .trim()
        .slice(tagUsShort.trim().length)
        .trim();
      if (replyWithEquivalent) {
        const localReplyPattern = new RegExp(
          `\\s*${escapeRegexSpecialChars(replyWithEquivalent)}\\s*$`,
          'i',
        );
        cleanedBefore = cleanedBefore.replace(localReplyPattern, '').trim();

        const replyWithTail = replyWithEquivalent.replace(/^\S+\s+/, '');
        if (replyWithTail && replyWithTail !== replyWithEquivalent) {
          const tailPattern = new RegExp(
            `\\s*${escapeRegexSpecialChars(replyWithTail)}\\s*$`,
            'i',
          );
          cleanedBefore = cleanedBefore.replace(tailPattern, '').trim();
        }
      }
    }

    return [cleanedBefore, `${tagUsSuffix}${approvalPhrase}`, afterPhrase]
      .filter(Boolean)
      .join(' ');
  }

  return message;
};

export const validateCustomUrl = (
  url: string,
  selectedOption: SelectedTermAndConditions,
): { hasError: boolean; errorMessage: string } => {
  if (selectedOption !== SelectedTermAndConditions.CustomLink) {
    return { hasError: false, errorMessage: '' };
  }
  if (!url || url.trim() === '') {
    return {
      hasError: true,
      errorMessage: CustomLinkTexts.ErrorMessages.EmptyUrl,
    };
  }
  if (!isValidUrl(url)) {
    return {
      hasError: true,
      errorMessage: CustomLinkTexts.ErrorMessages.InvalidUrl,
    };
  }
  // Clear errors if validation passes
  return {
    hasError: false,
    errorMessage: '',
  };
};

export const processHashtags = (
  rightsOptionFormState: RightsOptionFormState,
  message: string,
  extractedHashtags: string[],
) => {
  let updatedRightOptionFormState: RightsOptionFormState = {
    ...rightsOptionFormState,
  };
  if (extractedHashtags.length === 1) {
    // If there's only one hashtag, set it as the approval phrase
    const singleHashtag = extractedHashtags[0];
    updatedRightOptionFormState = {
      ...rightsOptionFormState,
      requestMessage: message,
      approvalPhrase: singleHashtag ?? '',
    };
  } else if (extractedHashtags.length > 1) {
    /**
     * If there are multiple hashtags, keep the request message
     * Select first approval phrase if it doesn't match any of the extracted hashtags
     * For e.g, if the message is "Requesting rights #hashtag1 #hashtag2 #hashtag3" and the selected approval phrase is "#hashtag2",
     * if the request message is updated to "Requesting rights #hashtag1 #hashtag3", we select the first approval phrase "#hashtag1"
     **/
    const currentApprovalPhrase = rightsOptionFormState.approvalPhrase;
    if (
      !currentApprovalPhrase ||
      !extractedHashtags.includes(currentApprovalPhrase)
    ) {
      const firstHashTag = extractedHashtags[0];
      updatedRightOptionFormState = {
        ...rightsOptionFormState,
        requestMessage: message,
        approvalPhrase: firstHashTag ?? '',
      };
    } else {
      // If the current approval phrase is valid, keep it
      updatedRightOptionFormState = {
        ...rightsOptionFormState,
        requestMessage: message,
      };
    }
  } else {
    // If no hashtags are found, just update the request message
    // and clear the approval phrase
    updatedRightOptionFormState = {
      ...rightsOptionFormState,
      requestMessage: message,
      approvalPhrase: '',
    };
  }
  return updatedRightOptionFormState;
};

export const validateRightsFormState = (
  rightsOptionFormState: RightsOptionFormState,
): RightsOptionErrorState => {
  const errors: RightsOptionErrorState = {};
  const { approvalPhrase, campaignTitle, requestMessage } =
    rightsOptionFormState;
  if (!campaignTitle.trim()) {
    errors.campaignTitle = 'You must provide a title';
  }
  if (!requestMessage.trim()) {
    errors.requestMessage = 'You must provide a request message';
  } else if (!approvalPhrase.trim()) {
    errors.requestMessage = 'You must provide an approval phrase';
  }
  return errors;
};

export const getUrlToAppend = (
  isRightsRequestTermsAndConditionsEnabled: boolean,
  tncUrl: string,
): string => {
  if (!isRightsRequestTermsAndConditionsEnabled) {
    return '';
  }
  return tncUrl;
};

export const getBaseRequestMessage = (
  messageData: IRightsRequestDatum | undefined,
  url: string,
  isRightsRequestTermsAndConditionsEnabled: boolean,
  isSyndicationAndTermsConditionsEnabled: boolean,
) => {
  let requestMessage = '';
  if (isDefined(messageData)) {
    requestMessage =
      isRightsRequestTermsAndConditionsEnabled && !!url
        ? getMessageWithoutUrl(messageData.message, url)
        : messageData.message;
    if (isSyndicationAndTermsConditionsEnabled) {
      // If syndication and rights terms and conditions are enabled, we need to remove the appended retailer message
      // from the request message when the message is edited.
      const retailerMsg = isDefined(messageData.languageCode)
        ? termsConditionsSettings[
            messageData.languageCode
          ]?.retailerMessages.filter((el: string) =>
            requestMessage.includes(el),
          )
        : [];
      if (isDefined(retailerMsg) && !!retailerMsg[0]) {
        // Remove the retailer message from the request message to get the base message
        requestMessage =
          requestMessage.split(retailerMsg[0])[0]?.trim() ?? requestMessage;
      }
    }
  }
  return requestMessage;
};

export const extractHashtags = (text: string): string[] => {
  if (!text.trim()) {
    return [];
  }
  return Array.from(new Set(text.match(hashtagRegex) ?? []));
};

const getMessageWithoutUrl = (originalMessage: string, url: string): string => {
  const urlWithoutProtocol = getUrlWithoutProtocol(url, true);
  const urlRegex = new RegExp(
    `${URL_PROTOCOL_PATTERN_WITH_WWW}${urlWithoutProtocol}`,
    'i',
  );
  return originalMessage.split(urlRegex)[0]?.trim() ?? originalMessage;
};

export const getTermsAndConditionsTitle = (
  isTncDefined: boolean,
  isTncEditable: boolean,
) => {
  if (!isTncDefined) {
    return TncTitles.SetupTnc;
  }
  if (isTncEditable) {
    return TncTitles.EditTnc;
  }
  return TncTitles.ViewTnc;
};

export const getCustomTncUrl = (termsAndConditions?: ITermsAndConditions) => {
  return isDefined(termsAndConditions) &&
    termsAndConditions.termsAndConditionsUrl !==
      getUrlWithoutProtocol(BV_TERMS_AND_CONDITIONS_URL, true)
    ? termsAndConditions.termsAndConditionsUrl
    : undefined;
};

const createRetailMessage = (
  baseMessage: string,
  languageCode: Locales,
  isSyndicationAndTermsConditionsEnabled: boolean,
  isRightsRequestTermsAndConditionsEnabled: boolean,
  retailerMessageIndex?: number,
  customTncUrl?: string,
  campaignTitle?: string,
): string => {
  // For TikTok general messaging, use "Learn more" instead of random retailer messages
  const isTikTokConfiguration = campaignTitle === GENERAL_MESSAGING_TIKTOK;

  const getRetailerMessage = () => {
    if (
      !isDefined(retailerMessageIndex) ||
      !isSyndicationAndTermsConditionsEnabled
    ) {
      return '';
    }
    if (isTikTokConfiguration) {
      return getTikTokRetailerMessage(languageCode);
    }
    return termsConditionsSettings[languageCode]?.retailerMessages[
      retailerMessageIndex
    ];
  };

  const retailerMessage = getRetailerMessage();

  // For TikTok configurations, append "tag us and reply with" to the approval phrase
  const [approvalPhrase] = isTikTokConfiguration
    ? extractHashtags(baseMessage)
    : [];

  const tncUrl = (() => {
    if (isDefined(customTncUrl)) {
      return customTncUrl;
    }
    if (termsConditionsSettings[languageCode]?.url) {
      return termsConditionsSettings[languageCode].url;
    }
    return termsConditionsSettings[Locales.EN_US]?.url ?? '';
  })();

  const tikTokTagUsSuffix = getTikTokTagUsSuffix(languageCode);

  const strippedMessage = (() => {
    let message = baseMessage;
    if (isTikTokConfiguration) {
      if (!hasTagUsPhrase(baseMessage, TIKTOK_TAG_US_SHORT_PHRASES)) {
        for (const phrase of TIKTOK_TAG_US_PHRASES) {
          const escaped = escapeRegexSpecialChars(phrase);
          const suffixPattern = new RegExp(
            `(?<![\\p{L}\\p{N}])\\s*${escaped}\\s*(?![\\p{L}\\p{N}])`,
            'gui',
          );
          message = message.replace(suffixPattern, ' ').trim();
        }
      }

      if (retailerMessage) {
        const retailerPattern = new RegExp(
          `\\s*${escapeRegexSpecialChars(retailerMessage)}\\s*`,
          'gi',
        );
        message = message.replace(retailerPattern, ' ').trim();
      }

      if (tncUrl) {
        const tncUrlWithoutProtocol = getUrlWithoutProtocol(tncUrl, true);
        const tncPattern = new RegExp(
          `\\s*${URL_PROTOCOL_PATTERN_WITH_WWW}?${escapeRegexSpecialChars(tncUrlWithoutProtocol)}\\s*`,
          'gi',
        );
        message = message.replace(tncPattern, ' ').trim();
      }

      message = message.replace(/\s+/g, ' ').trim();
    }
    return message;
  })();

  const modifiedMessage =
    approvalPhrase &&
    !strippedMessage.includes(`${tikTokTagUsSuffix}${approvalPhrase}`)
      ? appendTagUsSuffixToApprovalPhrase(
          strippedMessage,
          approvalPhrase,
          languageCode,
        )
      : strippedMessage;

  const urlToAppend = getUrlToAppend(
    isRightsRequestTermsAndConditionsEnabled,
    tncUrl,
  );

  const shouldAppendUrl = isTikTokConfiguration
    ? urlToAppend && !modifiedMessage.includes(urlToAppend)
    : !!urlToAppend;

  const messageComponents = [
    modifiedMessage,
    retailerMessage,
    shouldAppendUrl ? urlToAppend : '',
  ].filter(Boolean);
  return messageComponents.join(' ');
};

export const createNewBVConfiguration = (
  clientId: ClientId,
  isSyndicationAndTermsConditionsEnabled: boolean,
  isRightsRequestTermsAndConditionsEnabled: boolean,
  customTncUrl?: string,
) => {
  const bvNetworkConfig = BV_NETWORK_MESSAGES.map((baseMessage, index) => ({
    message: createRetailMessage(
      baseMessage,
      Locales.EN_US,
      isSyndicationAndTermsConditionsEnabled,
      isRightsRequestTermsAndConditionsEnabled,
      index,
      customTncUrl,
    ),
    approvalPhrase: '#agree',
    languageCode: Locales.EN_US,
  }));
  return {
    name: 'Bazaarvoice Network',
    clientId: clientId,
    isDefault: true,
    isNew: false,
    allowCustomRequest: true,
    targetType: RightsRequestTargetType.CLIENT,
    rightsRequestData: {
      data: bvNetworkConfig,
    },
  };
};

export const createNewTikTokConfiguration = (
  clientId: ClientId,
  isSyndicationAndTermsConditionsEnabled: boolean,
  isRightsRequestTermsAndConditionsEnabled: boolean,
  customTncUrl?: string,
) => {
  const tikTokNetworkConfig = TIKTOK_NETWORK_MESSAGES.map(
    (baseMessage, index) => ({
      message: createRetailMessage(
        baseMessage,
        Locales.EN_US,
        isSyndicationAndTermsConditionsEnabled,
        isRightsRequestTermsAndConditionsEnabled,
        index,
        customTncUrl,
        GENERAL_MESSAGING_TIKTOK,
      ),
      approvalPhrase: '#agree',
      languageCode: Locales.EN_US,
    }),
  );
  return {
    name: GENERAL_MESSAGING_TIKTOK,
    clientId: clientId,
    isDefault: true,
    isNew: false,
    allowCustomRequest: true,
    targetType: RightsRequestTargetType.CLIENT,
    rightsRequestData: {
      data: tikTokNetworkConfig,
    },
  };
};

const createMessageDataList = (
  rightsOptionFormState: RightsOptionFormState,
  selectedRightsOption?: IRightsOption,
  isSyndicationAndTermsConditionsEnabled = false,
  isRightsRequestTermsAndConditionsEnabled = false,
  customTncUrl?: string,
) => {
  let updatedMessageDataList = [
    ...(selectedRightsOption?.rightsRequestData.data ?? []),
  ];
  const {
    approvalPhrase,
    campaignTitle,
    languageCode,
    requestMessage,
    selectedIndex,
  } = rightsOptionFormState;
  const retailerMessages =
    termsConditionsSettings[languageCode]?.retailerMessages ?? [];
  const randomRetailIndex = Math.floor(Math.random() * retailerMessages.length);
  if (selectedIndex > -1) {
    const messageData = {
      ...updatedMessageDataList[selectedIndex],
      message: createRetailMessage(
        requestMessage,
        languageCode,
        isSyndicationAndTermsConditionsEnabled,
        isRightsRequestTermsAndConditionsEnabled,
        randomRetailIndex,
        customTncUrl,
        campaignTitle,
      ),
      languageCode,
      approvalPhrase,
    };
    updatedMessageDataList = [
      ...updatedMessageDataList.slice(0, selectedIndex),
      messageData,
      ...updatedMessageDataList.slice(selectedIndex + 1),
    ];
  } else {
    const messageData = {
      message: createRetailMessage(
        requestMessage,
        languageCode,
        isSyndicationAndTermsConditionsEnabled,
        isRightsRequestTermsAndConditionsEnabled,
        randomRetailIndex,
        customTncUrl,
        campaignTitle,
      ),
      languageCode,
      approvalPhrase,
    };
    updatedMessageDataList = [messageData, ...updatedMessageDataList];
  }
  return updatedMessageDataList;
};

export const createRightsOptionPayload = ({
  clientId,
  customTncUrl,
  isRightsRequestTermsAndConditionsEnabled,
  isSyndicationAndTermsConditionsEnabled,
  rightsOptionFormState,
  selectedRightsOption,
  updatedMessageDataList,
}: {
  clientId: ClientId;
  rightsOptionFormState: RightsOptionFormState;
  selectedRightsOption?: IRightsOption;
  isSyndicationAndTermsConditionsEnabled: boolean;
  isRightsRequestTermsAndConditionsEnabled: boolean;
  customTncUrl?: string;
  updatedMessageDataList?: IRightsRequestDatum[];
}) => {
  const basePayload = {
    clientId: clientId,
    isDefault: isDefined(selectedRightsOption)
      ? selectedRightsOption.isDefault
      : false,
    isNew: !isDefined(selectedRightsOption),
    targetType: RightsRequestTargetType.CLIENT,
    allowCustomRequest: true,
    id: isDefined(selectedRightsOption) ? selectedRightsOption.id : undefined,
  };
  if (isDefined(updatedMessageDataList) && isDefined(selectedRightsOption)) {
    return {
      ...basePayload,
      name: selectedRightsOption.name,
      rightsRequestData: {
        data: updatedMessageDataList,
      },
    };
  } else {
    const updatedMessageDataList = createMessageDataList(
      rightsOptionFormState,
      selectedRightsOption,
      isSyndicationAndTermsConditionsEnabled,
      isRightsRequestTermsAndConditionsEnabled,
      customTncUrl,
    );

    return {
      ...basePayload,
      name: rightsOptionFormState.campaignTitle,
      rightsRequestData: {
        data: updatedMessageDataList,
      },
    };
  }
};

export const isRightsMessagingContentLoading = (
  activeStep: RightsRequestConfigurationStep,
  loadingStates: {
    isConfigLoading: boolean;
    isTermsAndConditionLoading: boolean;
    isUpdatingTermsAndCondition: boolean;
    isUpdatingRightsConfigurations: boolean;
    isDeletingRightsConfiguration: boolean;
  },
) => {
  const {
    isConfigLoading,
    isDeletingRightsConfiguration,
    isTermsAndConditionLoading,
    isUpdatingRightsConfigurations,
    isUpdatingTermsAndCondition,
  } = loadingStates;
  switch (activeStep) {
    case RightsRequestConfigurationStep.SetupRightsMessaging:
      return (
        isConfigLoading ||
        isTermsAndConditionLoading ||
        isDeletingRightsConfiguration
      );
    case RightsRequestConfigurationStep.TermsAndConditions:
      return isTermsAndConditionLoading || isUpdatingTermsAndCondition;
    case RightsRequestConfigurationStep.RightsOptionsMessages:
      return (
        isConfigLoading ||
        isDeletingRightsConfiguration ||
        isUpdatingRightsConfigurations
      );
    case RightsRequestConfigurationStep.AddMessageForm:
    case RightsRequestConfigurationStep.RightsOptionForm:
      return isUpdatingRightsConfigurations;
    default:
      return false;
  }
};

export const getRightsMessagingModalTitle = (
  activeStep: RightsRequestConfigurationStep,
  rightsOptionFormState: RightsOptionFormState,
  isEditingRightsOptionTitle: boolean,
  isBazaarvoiceNetworkSyndicationFrontendEnabled: boolean,
  termsAndConditions?: ITermsAndConditions,
  selectedRightsOption?: IRightsOption,
) => {
  const isTncEditable =
    isDefined(termsAndConditions) &&
    termsAndConditions.isCustom &&
    !isBazaarvoiceNetworkSyndicationFrontendEnabled;
  switch (activeStep) {
    case RightsRequestConfigurationStep.SetupRightsMessaging:
      return 'Setup Message Templates';
    case RightsRequestConfigurationStep.RightsOptionsMessages:
      return selectedRightsOption?.name ?? '';
    case RightsRequestConfigurationStep.AddMessageForm:
      if (isEditingRightsOptionTitle) {
        return 'Edit Campaign Title';
      }
      return rightsOptionFormState.requestMessage
        ? 'Edit Message'
        : 'Add Message';
    case RightsRequestConfigurationStep.RightsOptionForm:
      return 'Create Message Template';
    case RightsRequestConfigurationStep.TermsAndConditions:
      return getTermsAndConditionsTitle(
        isDefined(termsAndConditions),
        isTncEditable,
      );
    default:
      return '';
  }
};

export const getPrimaryButtonInfo = (
  activeStep: RightsRequestConfigurationStep,
  handlers: {
    handleUpdateRightsConfig: () => void;
    backHandler: () => void;
    termsAndConditionSaveHandler: () => void;
  },
  loadingStates: RightsOptionsLoadingState,
  isEditingRightsOptionTitle: boolean,
  isBazaarvoiceNetworkSyndicationFrontendEnabled: boolean,
  hasError: boolean,
  termsAndConditions?: ITermsAndConditions,
  rightsOptionFormState?: RightsOptionFormState,
  isOverLimit?: boolean,
) => {
  const {
    backHandler,
    handleUpdateRightsConfig,
    termsAndConditionSaveHandler,
  } = handlers;
  const isTncEditable =
    isDefined(termsAndConditions) &&
    termsAndConditions.isCustom &&
    !isBazaarvoiceNetworkSyndicationFrontendEnabled;
  const {
    isConfigLoading,
    isDeletingRightsConfiguration,
    isDetectingLanguage,
    isTermsAndConditionLoading,
    isUpdatingRightsConfigurations,
    isUpdatingTermsAndCondition,
  } = loadingStates;
  const primaryButtonStates = {
    buttonContent: 'Save',
    isDisabled: true,
    trackingId: '',
  };
  switch (activeStep) {
    case RightsRequestConfigurationStep.TermsAndConditions:
      return {
        buttonContent: isUndefined(termsAndConditions) ? 'Accept' : 'Save',
        onClick: termsAndConditionSaveHandler,
        isDisabled:
          isConfigLoading ||
          isTermsAndConditionLoading ||
          isUpdatingTermsAndCondition ||
          !(isUndefined(termsAndConditions) || isTncEditable) ||
          hasError,
        permissionName: UIA_RIGHTS_REQUEST_PERMISSION,
        trackingId: SetupMessagingTrackingIds.TERMS_AND_CONDITIONS_SAVE_BUTTON,
      };
    case RightsRequestConfigurationStep.RightsOptionsMessages:
      return {
        buttonContent: 'Back',
        onClick: backHandler,
        isDisabled:
          isConfigLoading ||
          isUpdatingRightsConfigurations ||
          isDeletingRightsConfiguration,
        buttonStyles: SecondaryButtonStyles,
        trackingId:
          SetupMessagingTrackingIds.RIGHTS_OPTIONS_MESSAGES_BACK_BUTTON,
      };
    case RightsRequestConfigurationStep.AddMessageForm:
      return {
        ...primaryButtonStates,
        onClick: handleUpdateRightsConfig,
        isDisabled:
          isConfigLoading ||
          isUpdatingRightsConfigurations ||
          isDetectingLanguage ||
          isOverLimit ||
          (rightsOptionFormState &&
            (!rightsOptionFormState.campaignTitle.trim() ||
              !rightsOptionFormState.requestMessage.trim())),
        trackingId: isEditingRightsOptionTitle
          ? SetupMessagingTrackingIds.EDIT_CAMPAIGN_TITLE_SAVE_BUTTON
          : SetupMessagingTrackingIds.ADD_RIGHTS_OPTION_MESSAGE_SAVE_BUTTON,
      };
    case RightsRequestConfigurationStep.RightsOptionForm:
      return {
        ...primaryButtonStates,
        onClick: handleUpdateRightsConfig,
        isDisabled:
          isConfigLoading ||
          isUpdatingRightsConfigurations ||
          isDetectingLanguage ||
          isOverLimit ||
          (rightsOptionFormState &&
            (!rightsOptionFormState.campaignTitle.trim() ||
              !rightsOptionFormState.requestMessage.trim())),
        buttonContent: 'Add Campaign',
        trackingId: SetupMessagingTrackingIds.ADD_RIGHTS_OPTION_SAVE_BUTTON,
      };
    case RightsRequestConfigurationStep.SetupRightsMessaging:
      return primaryButtonStates;
    default:
      return primaryButtonStates;
  }
};

export const getSecondaryButtonInfo = (
  activeStep: RightsRequestConfigurationStep,
  handler: () => void,
  loadingStates: RightsOptionsLoadingState,
  isEditingRightsOptionTitle: boolean,
) => {
  const secondaryButtonStates = {
    buttonContent: 'Back',
    onClick: handler,
  };
  const {
    isConfigLoading,
    isDetectingLanguage,
    isTermsAndConditionLoading,
    isUpdatingRightsConfigurations,
    isUpdatingTermsAndCondition,
  } = loadingStates;
  switch (activeStep) {
    case RightsRequestConfigurationStep.TermsAndConditions:
      return {
        ...secondaryButtonStates,
        isDisabled: isTermsAndConditionLoading || isUpdatingTermsAndCondition,
        trackingId: SetupMessagingTrackingIds.TERMS_AND_CONDITIONS_BACK_BUTTON,
      };
    case RightsRequestConfigurationStep.AddMessageForm:
      return {
        ...secondaryButtonStates,
        isDisabled:
          isConfigLoading ||
          isUpdatingRightsConfigurations ||
          isDetectingLanguage,
        trackingId: isEditingRightsOptionTitle
          ? SetupMessagingTrackingIds.EDIT_CAMPAIGN_TITLE_BACK_BUTTON
          : SetupMessagingTrackingIds.ADD_RIGHTS_OPTION_MESSAGE_BACK_BUTTON,
      };
    case RightsRequestConfigurationStep.RightsOptionForm:
      return {
        ...secondaryButtonStates,
        isDisabled:
          isConfigLoading ||
          isUpdatingRightsConfigurations ||
          isDetectingLanguage,
        trackingId: SetupMessagingTrackingIds.ADD_RIGHTS_OPTION_BACK_BUTTON,
      };
    case RightsRequestConfigurationStep.SetupRightsMessaging:
    case RightsRequestConfigurationStep.RightsOptionsMessages:
      return undefined; // No secondary button for steps
    default:
      return secondaryButtonStates;
  }
};

export const getDetectedLanguageLocale = (languageCode?: string): Locales => {
  if (!languageCode) {
    return Locales.EN_US;
  }
  const matchedLocale = RIGHTS_REQUEST_LOCALES.find((locale) => {
    return locale.value.toLowerCase().startsWith(languageCode.toLowerCase());
  });
  return matchedLocale ? matchedLocale.value : Locales.EN_US;
};
